import { useState, type FormEvent } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAppStore } from "@/lib/store";
import RevealOnScroll from "@/components/ui/RevealOnScroll";
import ImmersiveBackground from "@/components/ui/ImmersiveBackground";
import { useDocumentMeta } from "@/lib/useDocumentMeta";

export default function LoginPage() {
  useDocumentMeta({
    title: "Sign In",
    description: "Sign in to your Nerd Multiverse account.",
    path: "/login",
    noindex: true,
  });

  const navigate = useNavigate();
  const login = useAppStore((s) => s.login);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      const user = await login(email.trim(), password);
      navigate(
        user.role === "admin"
          ? "/dashboard/admin"
          : user.role === "seller"
          ? "/dashboard/seller"
          : "/marketplace"
      );
    } catch {
      setError("Couldn't sign in with that email and password.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="relative overflow-hidden min-h-dvh flex items-center md:block pt-20 pb-8 md:pt-32 md:pb-24">
      <ImmersiveBackground
        src="/images/hero/login-v2.webp"
        mobileSrc="/images/hero/login-mobile-v2.webp"
        focus="50% 24%"
      />
      <div className="nm-container relative w-full">
        <div className="mx-auto w-full max-w-md md:max-w-xl lg:max-w-2xl">
          <RevealOnScroll>
            <span className="hidden md:inline-flex nm-kicker">Welcome back</span>
            <h1 className="md:mt-5 font-display text-2xl md:text-5xl font-black tracking-[-.04em] text-white">
              Sign in
            </h1>

            <div className="nm-glass mt-4 md:mt-6 rounded-2xl p-4 md:p-10">
              <form onSubmit={handleSubmit} className="space-y-3 md:space-y-5">
                <div>
                  <label className="sr-only md:not-sr-only md:block text-sm md:text-base text-[var(--nm-ink-muted)] md:mb-2">
                    Email
                  </label>
                  <input
                    type="email"
                    required
                    placeholder="Email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full px-4 py-2.5 md:py-4 md:text-lg rounded-lg bg-white/5 border border-white/10 text-white placeholder:text-[var(--nm-ink-muted)] focus:outline-none focus:border-[var(--nm-blue-bright)] transition-colors"
                  />
                </div>
                <div>
                  <label className="sr-only md:not-sr-only md:block text-sm md:text-base text-[var(--nm-ink-muted)] md:mb-2">
                    Password
                  </label>
                  <input
                    type="password"
                    required
                    placeholder="Password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full px-4 py-2.5 md:py-4 md:text-lg rounded-lg bg-white/5 border border-white/10 text-white placeholder:text-[var(--nm-ink-muted)] focus:outline-none focus:border-[var(--nm-blue-bright)] transition-colors"
                  />
                </div>
                {error && <p className="text-sm text-[var(--nm-pink)]">{error}</p>}
                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full py-2.5 md:py-4 rounded-full bg-white text-[#0c1222] text-sm md:text-base font-medium tracking-wide hover:bg-[var(--nm-blue-bright)] hover:text-white transition-colors duration-200 disabled:opacity-50"
                >
                  {submitting ? "SIGNING IN…" : "SIGN IN"}
                </button>
              </form>
            </div>

            <p className="mt-4 md:mt-6 text-sm md:text-base text-[var(--nm-ink-muted)]">
              New here?{" "}
              <Link to="/signup" className="text-white underline underline-offset-4">
                Create an account
              </Link>
            </p>
          </RevealOnScroll>
        </div>
      </div>
    </div>
  );
}
